import { isArray, isObject } from "../../../../utils/zone.utils";
import { discardProps } from "../../../useDiscard";
import useDebouncing from "../../../utils/useDebouncing";
import useMap from "../../../utils/useMap"
import useWeakMap from "../../../utils/useWeakMap";
import { useCompareIsEqual } from "../../utils/useCompare";
import { isLayer } from "../validator/useLayerIdentity";
import useUpdateLayerByOriginalSource from "../updater/useUpdateLayerByOriginalSource";
import useUpdateOriginalSource from "../updater/useUpdateOriginalSource";
import useLayerQueryByPath, { checkHasLayerByPath, countPath, deleteByChainId, hasLayerByPath, queryAllLayersAsArray, storeByPath } from "./useStoreByPath";
import useQueryCopySource, { storeCopySource } from "./useStoreCopySource.js";
import useQueryPathByLayer from "./useStorePathByLayer";
import useQueryOriginalSourceByLayer, { storeOriginalSourceByLayer } from "./useStoreOriginalSource";
import useQueryLayerUsingOriginalSource, { storeLayerUsingOriginalSource } from "./useStoreLayerUsingOriginalSource";
import useQueryLinkingLayers, { storeLinkingLayer as hookStoreRefLayer, storeLinkingLayer } from "./useStoreRefLayer";
import { deleteSubChainByChainId, storeSubChain as hookStoreSubChain } from "./useStoreSubChain";
import useLogStack from "../../utils/useLogStack";

const store = useMap();

const storeLayers = new Set();

const generateChainId = (() => {
    let count = 1;
    return Symbol(`chain[${++count}]`)
})()

const deleteSubChain = (parentChainId) => {
    const subChainIds = subChains.get(parentChainId);
    const chainPool = getData(KEY_LAYER_CHAIN);
    if (subChainIds) {
        subChainIds.forEach(id => {
            if (chainPool.get(id)) {
                chainPool.get(id).discardChain();
            }
        })
        subChains.delete(parentChainId);
    }
}

const doUpdateLayer = (chainId, propPath, key, value, changedLayer) => {
    // if(changedLayer[key] === value) return;
    // Object.assign(changedLayer[key], value)
    const copySource = useQueryCopySource(changedLayer),
        copySourceByValue = useQueryCopySource(value);

    const clonePath = propPath.clone();
    clonePath.push(key);
    const layer = useLayerQueryByPath(chainId, clonePath);

    if (layer) {
        // debugger;
        const o = Object.assign(layer, value);
        // copySource[key] = value;
        copySource[key] = o;
    }
}

const doUpdateObject = (chainId, propPath, key, value, changedLayer) => {
    /**
       * IMPORTANT
       * If the prop 'k' is a proxy, need query Proxy then re-update value, make sure not assign new object. Because it will break the reference
       */

    const copySource = useQueryCopySource(changedLayer);
    if (value && typeof value === 'object' && isLayer(copySource[key]) && !isLayer(value)) {
        const clone = propPath.clone();
        value = Object.assign(copySource[key], value);
        clone.push(key);
        const layer = useLayerQueryByPath(chainId, clone);
        if (layer) {
            return Object.assign(layer, value);
        }
        return value;
    }
}

const syncOriginalSource = (originalSource, key, value) => {
    if (!originalSource) return;
    if (!(key in originalSource)) return;

    if (isLayer(originalSource)) {
        originalSource = useQueryOriginalSourceByLayer(value);
    }

    if (!isLayer(value)) {
        originalSource[key] = value;
        return;
    }

    if (isLayer(value)) {
        value = useQueryOriginSource(value)
    }

    if (originalSource && value) {
        originalSource[key] = value;
    }

}

/**
 * 
 * @param {string} chainId 
 * @param {PropArray} propPaths 
 * @param {Object} orginalSource 
 * @param {typeof Proxy} layer 
 */
const useStoreChain = (chainId) => {

    const fnEmiChanges = new Set();

    if (store.has(chainId)) {
        return store.get(chainId);
    }

    const storeLayer = (propertyPath, orginalSource, copySource, layer) => {
        storeLayerUsingOriginalSource(orginalSource, layer)

        storeLinkingLayer(propertyPath, layer);

        if (!hasLayer(propertyPath)) {
            storeLayers.add(layer);
            storeByPath(chainId, propertyPath, layer);
            storeCopySource(layer, copySource);
            storeOriginalSourceByLayer(layer, orginalSource);
            if (propertyPath.length === 0 && getRootLayer() !== layer) {
                storeOriginalSourceByLayer(getRootLayer(), orginalSource);
            }
        }
    }

    const notifyLayerChanges = (propertyPath, key, value, changedLayer) => {
        let result = false;
        const targetLayer = useLayerQueryByPath(chainId, propertyPath),
            copySource = useQueryCopySource(targetLayer),
            originalSource = useQueryOriginalSourceByLayer(targetLayer);

        const originSourceByChangedLayer = useQueryOriginalSourceByLayer(changedLayer);
        const copySourceByChangedLayer = useQueryCopySource(changedLayer);

        useUpdateOriginalSource(propertyPath, changedLayer, key, value);
        useUpdateLayerByOriginalSource(propertyPath, originSourceByChangedLayer, key, value, changedLayer);

        if (originalSource !== originSourceByChangedLayer) {
            useUpdateOriginalSource(propertyPath, targetLayer, key, value);
            useUpdateLayerByOriginalSource(propertyPath, originalSource, key, value);
        }

        emitRootChanges();
        return true;
    }

    const queryLayerByPath = (propsPath) => {
        if (!propsPath) return;
        return useLayerQueryByPath(chainId, propsPath);
    }

    const queryOriginSourceByPath = (propsPath) => {
        const layer = queryLayerByPath(propsPath);
        if (!layer) return;
        return useQueryOriginalSourceByLayer(layer);
    }

    const queryCopySourceByPath = (propPaths) => {
        const layer = queryLayerByPath(propsPath);
        if (!layer) return;
        return useQueryCopySource(layer);
    }

    const queryParentLayerByPath = (propPaths) => {
        propPaths = propPaths.clone();
        propPaths.pop();
        return queryLayerByPath(propPaths);
    }

    const getOriginalSource = () => {
        const layer = useLayerQueryByPath(chainId, '');
        return useQueryOriginalSourceByLayer(layer);
    }

    const getRootLayer = () => {
        return useLayerQueryByPath(chainId, '');
    }

    const getRootSource = () => {
        const rootLayer = getRootLayer();
        if (!rootLayer) return;
        return useQueryCopySource(rootLayer);
    }

    const storeLinkingLayer = (propPaths, refLayer) => {
        const layer = queryLayerByPath(propPaths);
        if (!layer) return;
        if (layer === refLayer) return;
        hookStoreRefLayer(layer, refLayer);
    }

    const getChainId = () => chainId

    const isEmpty = () => countPath(chainId) === 0;

    const hasLayer = (propPaths) => checkHasLayerByPath(chainId, propPaths);

    const hasParentLayer = (propPaths) => {
        const clonePath = propPaths.clone();
        clonePath.pop();
        return hasLayer(clonePath);
    }

    const discardChain = () => {
        // const chainPool = getData(KEY_LAYER_CHAIN);
        const root = getRootLayer();
        const allLayers = queryAllLayersAsArray(chainId);
        while (allLayers.length > 0) {
            const layer = allLayers.shift();
            const originSource = useQueryOriginalSourceByLayer(layer),
                copySource = useQueryCopySource(layer);
            discardProps(layer);
        }
        deleteSubChainByChainId(chainId);
        store.delete(chainId);
    }

    const storeSubChain = (subChainId) => {
        hookStoreSubChain(chainId, subChainId)
    }

    const onRootNotifyEmitChanges = (cb) => {
        fnEmiChanges.add(cb)
    }

    const emitRootChanges = useDebouncing(() => {
        fnEmiChanges.forEach(cb => {
            cb.call(null)
        })
    })

    store.set(chainId, {
        storeLayer,
        notifyLayerChanges,
        queryLayerByPath,
        queryOriginSourceByPath,
        queryCopySourceByPath,
        queryParentLayerByPath,
        storeLinkingLayer,
        getRootLayer,
        getRootSource,
        getOriginalSource,
        isEmpty,
        hasLayer,
        hasParentLayer,
        getChainId,
        discardChain,
        storeSubChain,
        onRootNotifyEmitChanges
    })

    return store.get(chainId)
}

export default useStoreChain;